import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LIVE_INTERVAL_MS, useVisibleInterval } from "../lib/polling";
import {
  Box, Stack, Typography, Alert, AlertTitle, CircularProgress, IconButton, Tooltip,
  FormControlLabel, Switch, Paper, Chip, ToggleButton, ToggleButtonGroup, Divider,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ApiProbePanel from "../components/ApiProbePanel";
import { fmtArDateTime } from "../services/monitoring";
import {
  getEnlacesEstado, getEnlacesHistory, fmtDuration, fmtUptime, fmtResolution,
  type EnlacesEstado, type EnlacesHistory, type Enlace, type Tunnel,
  type EnlaceState, type InstanceState, type OutageEvent,
} from "../services/monitoreoEnlaces";

const RANGES = [7, 30, 90, 400];
const DEFAULT_RANGE = 400;   // el problema de los túneles 2 sólo se ve en la ventana larga

const C_OK    = "#16A34A";
const C_WARN  = "#B45309";
const C_DOWN  = "#DC2626";
const C_UNK   = "#94A3B8";

const ENLACE_COLOR: Record<EnlaceState, string> = { ok: C_OK, degraded: C_WARN, down: C_DOWN };
const ENLACE_TEXT:  Record<EnlaceState, string> = {
  ok: "Redundante", degraded: "Sin redundancia", down: "CAÍDO",
};
const INST_COLOR: Record<InstanceState, string> = { ok: C_OK, failed: C_DOWN, unknown: C_UNK };
const INST_TEXT:  Record<InstanceState, string> = { ok: "OK", failed: "FALLA", unknown: "sin datos" };

// "stp" con textTransform:capitalize da "Stp". Son siglas, van derecho.
const ENLACE_LABEL: Record<string, string> = { bind: "Bind", stp: "STP", coelsa: "Coelsa", cmf: "Banco CMF" };

function Dot({ color, size = 10 }: { color: string; size?: number }) {
  return <Box sx={{ width: size, height: size, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />;
}

/** Franja diaria estilo status page. Un rectángulo por día. */
function UptimeStrip({ days }: { days: { d: string; uptime: number }[] }) {
  if (!days.length) return <Typography variant="caption" color="text.secondary">Sin datos</Typography>;
  return (
    <Stack direction="row" spacing={0.25} sx={{ height: 26, alignItems: "stretch" }}>
      {days.map((d) => {
        const color = d.uptime >= 1 ? C_OK : d.uptime > 0 ? C_WARN : C_DOWN;
        const label = d.uptime >= 1
          ? "sin cortes"
          : d.uptime > 0
            ? `${fmtUptime(d.uptime)} — ${fmtDuration((1 - d.uptime) * 86400)} caído`
            : "caído todo el día";
        return (
          <Tooltip key={d.d} title={`${d.d}: ${label}`} arrow>
            <Box sx={{
              flex: "1 1 0", minWidth: 2, bgcolor: color, borderRadius: 0.5,
              opacity: 0.85, "&:hover": { opacity: 1 },
            }} />
          </Tooltip>
        );
      })}
    </Stack>
  );
}

function TunnelRow({ t }: { t: Tunnel }) {
  const up = t.state === "up";
  // El caso que motivó toda esta página: días con datos pero ninguno con uptime.
  const nuncaArriba = t.days_any_up === 0 && t.lookback_days > 0;

  let desde: string;
  if (nuncaArriba) desde = `nunca estuvo arriba en ${t.lookback_days} días`;
  else if (t.since_capped) desde = `hace más de ${t.lookback_days} días`;
  else if (!t.since) desde = "—";
  else {
    const d = new Date(t.since);
    desde = t.since_precision === "day"
      ? `desde el ${d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit" })} (precisión diaria)`
      : `desde ${fmtArDateTime(t.since)}`;
  }

  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 0.75 }} flexWrap="wrap" useFlexGap>
      <Dot color={up ? C_OK : C_DOWN} />
      <Typography sx={{ fontWeight: 700, minWidth: 28 }}>T{t.n}</Typography>
      <Typography variant="body2" sx={{ fontFamily: "monospace", minWidth: 130, color: "text.secondary" }}>
        {t.ip}
      </Typography>
      <Chip
        size="small"
        label={up ? "UP" : "DOWN"}
        sx={{ bgcolor: up ? C_OK : C_DOWN, color: "#fff", fontWeight: 700, height: 20 }}
      />
      <Typography variant="body2" color={nuncaArriba ? C_DOWN : "text.secondary"}
                  sx={{ fontWeight: nuncaArriba ? 700 : 400 }}>
        {desde}
      </Typography>
      <Box sx={{ flexGrow: 1 }} />
      <Typography variant="caption" color="text.secondary">
        30 d: <strong>{fmtUptime(t.uptime_30d)}</strong>
        {"  ·  "}
        {t.lookback_days} d: <strong>{fmtUptime(t.uptime_lookback)}</strong>
      </Typography>
      {t.source && (
        <Typography variant="caption" color="text.secondary" sx={{ width: "100%", pl: 3.25 }}>
          {t.source}
        </Typography>
      )}
    </Stack>
  );
}

function EnlaceCard({ e }: { e: Enlace }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderLeft: `4px solid ${ENLACE_COLOR[e.state]}` }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>{e.label}</Typography>
        <Chip size="small"
              label={e.single_tunnel && e.state === "degraded" ? "Sin redundancia (túnel único)" : ENLACE_TEXT[e.state]}
              sx={{ bgcolor: ENLACE_COLOR[e.state], color: "#fff", fontWeight: 700 }} />
        <Typography variant="body2" color="text.secondary">
          {e.tunnels_total === 1
            ? (e.tunnels_up === 1 ? "único túnel arriba" : "único túnel caído")
            : `${e.tunnels_up} de ${e.tunnels_total} túneles arriba`}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary" }}>
          {e.vpn_id}
        </Typography>
      </Stack>
      <Divider />
      <Box sx={{ mt: 0.5 }}>
        {e.tunnels.map((t) => <TunnelRow key={t.ip} t={t} />)}
      </Box>
      {e.key === "bind" && <ApiProbePanel provider="bind" />}
      {e.key === "cmf" && <ApiProbePanel provider="cmf" />}
      {e.key === "coelsa" && <ApiProbePanel provider="coelsa" />}
    </Paper>
  );
}

function EventsTable({ rows, resolution }: {
  rows: (OutageEvent & { serie: string })[];
  resolution: number;
}) {
  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        Sin eventos de caída en el rango.
      </Typography>
    );
  }
  // Con buckets gruesos la ventana es mucho más ancha que el corte real: un corte
  // de 2 min dentro de un bucket diario se ve como una ventana de 24 h. El dato
  // honesto es la columna Downtime, así que hay que decirlo en vez de que parezca
  // que el enlace estuvo caído un día entero.
  const aproximada = resolution > 60;
  const fmt = (iso: string) =>
    resolution >= 86400
      ? new Date(iso).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "2-digit" })
      : fmtArDateTime(iso);
  return (
    <>
      {aproximada && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          La ventana es el bucket de {fmtResolution(resolution)} donde cayó el corte, no su duración.
          El tiempo real caído es la columna <strong>Downtime</strong>. Para ventanas exactas, usá el rango de 7 días.
        </Typography>
      )}
      <TableContainer sx={{ maxHeight: 420 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Serie</TableCell>
              <TableCell>{aproximada ? "Ventana desde" : "Desde"}</TableCell>
              <TableCell>{aproximada ? "Ventana hasta" : "Hasta"}</TableCell>
              <TableCell align="right">Downtime</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={`${r.serie}-${r.from}-${i}`}>
                <TableCell sx={{ fontWeight: 600 }}>{r.serie}</TableCell>
                <TableCell>{fmt(r.from)}</TableCell>
                <TableCell>{fmt(r.to)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtDuration(r.downtime_s)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

export default function MonitoreoEnlaces() {
  const [data, setData] = useState<EnlacesEstado | null>(null);
  const [hist, setHist] = useState<EnlacesHistory | null>(null);
  const [days, setDays] = useState(DEFAULT_RANGE);
  const [loading, setLoading] = useState(false);
  const [histLoading, setHistLoading] = useState(false);
  const [histErr, setHistErr] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [auto, setAuto] = useState(true);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      setData(await getEnlacesEstado());
      setUpdatedAt(new Date().toISOString());
    } catch (e: any) {
      setErr(e?.message ?? "Error al cargar");
    } finally {
      setLoading(false);
    }
  }, []);

  // Secuencia de pedidos: si el usuario cambia de rango mientras hay uno en vuelo,
  // la respuesta vieja puede llegar después de la nueva. Sin este guard, la página
  // termina mostrando los datos de un rango con el botón de otro seleccionado.
  const reqSeq = useRef(0);

  const loadHist = useCallback(async (d: number) => {
    const seq = ++reqSeq.current;
    setHistLoading(true);
    setHistErr(null);
    try {
      const r = await getEnlacesHistory(d);
      if (seq !== reqSeq.current) return;          // llegó tarde: ya hay otro pedido
      setHist(r);
    } catch (e: any) {
      if (seq !== reqSeq.current) return;
      // Importante limpiar: dejar el histórico anterior haría que los datos de un
      // rango se muestren rotulados con el rango nuevo.
      setHist(null);
      setHistErr(e?.message ?? "Error al cargar el histórico");
    } finally {
      if (seq === reqSeq.current) setHistLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadHist(days); }, [loadHist, days]);
  useVisibleInterval(load, LIVE_INTERVAL_MS, auto);

  // Túneles que nunca estuvieron arriba en toda la ventana: no es una caída, es
  // redundancia que nunca existió. Merece un cartel propio, no una fila más.
  const nuncaArriba = useMemo(
    () => (data?.enlaces ?? []).flatMap((e) =>
      e.tunnels.filter((t) => t.days_any_up === 0 && t.lookback_days > 0)
        .map((t) => ({ enlace: e.label, t }))),
    [data],
  );

  const enlacesCaidos = useMemo(
    () => (data?.enlaces ?? []).filter((e) => e.state === "down"),
    [data],
  );

  // El backend devuelve el rango que respondió. Sólo se pinta si coincide con el
  // seleccionado: así es imposible mostrar datos de un rango rotulados con otro,
  // sea por respuesta fuera de orden, por error o por lo que sea.
  const histOk = !!hist && hist.days === days;

  const eventRows = useMemo(() => {
    if (!hist) return [];
    const rows = [
      ...hist.tunnels.flatMap((t) =>
        t.events.map((e) => ({ ...e, serie: `${t.enlace_label} T${t.n}` }))),
      ...hist.instances.flatMap((i) =>
        i.events.map((e) => ({ ...e, serie: i.label }))),
    ];
    return rows.sort((a, b) => b.from.localeCompare(a.from));
  }, [hist]);

  // La resolución con la que se lee la tabla de eventos es la más gruesa de
  // las series que la alimentan: si Coelsa viene a 5 min, sus ventanas son de
  // 5 min aunque el rango global sea de 1 min.
  const eventsResolution = useMemo(
    () => hist ? Math.max(hist.resolution_s, ...hist.tunnels.map((t) => t.resolution_s ?? 0)) : 60,
    [hist],
  );

  return (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} flexWrap="wrap">
        <Box>
          <Typography variant="h5">Enlaces bancarios</Typography>
          <Typography variant="body2" color="text.secondary">
            Estado de los túneles con Bind, STP y Coelsa, y de las instancias que son punto de falla único.
            Se lee directo de la métrica de CloudWatch: no depende de que existan alarmas.
            El túnel de Coelsa no tiene métrica propia: se infiere de la latencia de la Lambda que lo atraviesa.
          </Typography>
        </Box>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" sx={{ mb: 2 }} useFlexGap>
        <FormControlLabel
          control={<Switch checked={auto} onChange={(e) => setAuto(e.target.checked)} />}
          label={`Auto-refresh (${LIVE_INTERVAL_MS / 1000}s)`}
        />
        <Tooltip title="Refrescar ahora">
          <span><IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton></span>
        </Tooltip>
        {loading && <CircularProgress size={18} />}
        {updatedAt && (
          <Typography variant="caption" color="text.secondary">
            Última actualización: {fmtArDateTime(updatedAt)}
          </Typography>
        )}
      </Stack>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {enlacesCaidos.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Corte total de operatoria bancaria</AlertTitle>
          {enlacesCaidos.map((e) =>
            `${e.label}: ${e.tunnels_total === 1 ? "su único túnel está caído" : "los dos túneles caídos"}`,
          ).join(" · ")}
        </Alert>
      )}

      {nuncaArriba.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Redundancia inexistente, no degradada</AlertTitle>
          {nuncaArriba.map(({ enlace, t }) => (
            <div key={t.ip}>
              <strong>{enlace} túnel {t.n}</strong> ({t.ip}) no estuvo arriba{" "}
              ni un solo día en los últimos {t.lookback_days}.
            </div>
          ))}
          <Box sx={{ mt: 1 }}>
            Estos enlaces operan sobre un único túnel. Cualquier caída del túnel que
            queda es un corte total: no hay failover al que caer.
          </Box>
        </Alert>
      )}

      {data && (
        <Stack spacing={2}>
          {data.enlaces.map((e) => <EnlaceCard key={e.key} e={e} />)}

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
              Instancias — puntos de falla único
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Instancia</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Enlace</TableCell>
                  <TableCell>AZ</TableCell>
                  <TableCell>Rol</TableCell>
                  <TableCell align="right">Checks fallidos (6 h)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.instances.map((i) => (
                  <TableRow key={i.key}>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Dot color={INST_COLOR[i.state]} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{i.label}</Typography>
                          <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                            {i.instance_id}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={INST_TEXT[i.state]}
                            sx={{ bgcolor: INST_COLOR[i.state], color: "#fff", fontWeight: 700, height: 20 }} />
                    </TableCell>
                    <TableCell>{ENLACE_LABEL[i.enlace] ?? i.enlace}</TableCell>
                    <TableCell>{i.az}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{i.rol}</Typography>
                    </TableCell>
                    <TableCell align="right">{i.failed_checks_6h}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Histórico</Typography>
              <ToggleButtonGroup
                size="small" exclusive value={days}
                onChange={(_e, v) => { if (v) setDays(v); }}
              >
                {RANGES.map((d) => (
                  <ToggleButton key={d} value={d}>{d} d</ToggleButton>
                ))}
              </ToggleButtonGroup>
              {histLoading && <CircularProgress size={18} />}
              {histOk && (
                <Typography variant="caption" color="text.secondary">
                  Resolución de detección: {fmtResolution(hist!.resolution_s)}.
                  {hist!.resolution_s > 60 &&
                    " CloudWatch retiene el dato de 1 minuto sólo 15 días, así que los rangos largos usan buckets más gruesos."}
                </Typography>
              )}
            </Stack>

            {histErr && <Alert severity="error" sx={{ mb: 2 }}>{histErr}</Alert>}
            {!histOk && !histErr && (
              <Typography variant="body2" color="text.secondary">
                Cargando histórico de {days} días…
              </Typography>
            )}

            {histOk && (
              <Stack spacing={1.5}>
                {hist.tunnels.map((t) => (
                  <Box key={`${t.enlace}-${t.n}`}>
                    <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 90 }}>
                        {t.enlace_label} T{t.n}
                      </Typography>
                      <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                        {t.ip}
                      </Typography>
                      <Box sx={{ flexGrow: 1 }} />
                      <Typography variant="caption" color="text.secondary">
                        uptime <strong>{fmtUptime(t.uptime)}</strong> · {t.events.length} evento(s)
                        {t.resolution_s > hist!.resolution_s && ` · resolución ${fmtResolution(t.resolution_s)}`}
                      </Typography>
                    </Stack>
                    <UptimeStrip days={t.days} />
                    {t.enlace === "coelsa" && t.days.length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Coelsa no tiene métrica propia del túnel. La serie por timeouts existe desde el
                        04/09/2026 y a resolución horaria no hay relleno por latencia (daría falsos cortes),
                        así que en rangos largos sólo aparece lo posterior a esa fecha.
                      </Typography>
                    )}
                    {t.enlace === "cmf" && t.days.length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Banco CMF no tiene VPN: la serie es de errores de conexión del connector hacia el banco o el
                        proxy Squid y existe desde el 03/11/2026; a resolución horaria no hay relleno por latencia,
                        así que en rangos largos sólo aparece lo posterior a esa fecha.
                      </Typography>
                    )}
                  </Box>
                ))}

                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Eventos de caída ({eventRows.length})
                </Typography>
                <EventsTable rows={eventRows} resolution={eventsResolution} />
              </Stack>
            )}
          </Paper>
        </Stack>
      )}
    </Box>
  );
}
